import { useState, useCallback, useEffect } from 'react';
import type { BoxProps } from '@mui/material/Box';
import debounce from 'lodash.debounce';

import type { NavSectionProps } from 'src/components/nav-section';

import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

import {
  Box,
  Dialog,
  InputBase,
  InputAdornment,
  SvgIcon,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  CircularProgress,
  Stack,
  IconButton,
} from '@mui/material';
import { PulseLoader } from 'react-spinners';

import { ResultItem } from './result-item';
import { useBoolean } from 'src/hooks/use-boolean';
import { useEventListener } from 'src/hooks/use-event-listener';
import { varAlpha } from 'src/theme/styles';
import { Scrollbar } from 'src/components/scrollbar';
import { Iconify } from 'src/components/iconify';
import { Label } from 'src/components/label';
import { SearchNotFound } from 'src/components/search-not-found';
import { toast } from 'src/components/snackbar';
import EncryptUtils from 'src/utils/EncryptUtils';
import { chatDataService, getAdminPageForm } from 'src/services/service-handlers/PrivateService';
import { FormLoader } from 'src/components/form-builder';

// ----------------------------------------------------------------------

export type SearchbarProps = BoxProps & {
  data?: NavSectionProps['data'];
  onClose?: () => void;
  onSelectPerson?: (person: any) => void;
};

export function Searchbar({
  data: navItems = [],
  onClose,
  onSelectPerson,
  sx,
  ...other
}: SearchbarProps) {
  const theme = useTheme();
  const search = useBoolean();

  const [searchQuery, setSearchQuery] = useState('');
  const [peopleResults, setPeopleResults] = useState<any[]>([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const [adminFormData, setAdminFormData] = useState<any>(null);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'k' && event.metaKey) {
      search.onToggle();
      setSearchQuery('');
    }
  };

  useEventListener('keydown', handleKeyDown);

  const handleClose = useCallback(() => {
    search.onFalse();
    setSearchQuery('');
    setPeopleResults([]);
  }, [search]);

  const handleSearch = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSelectPerson = async (person: any) => {
    if (onSelectPerson) {
      onSelectPerson(person);
      handleClose();
      return;
    }

    setLoading(true);
    try {
      const formRes = await getAdminPageForm('person-details');
      setAdminFormData(formRes);
      setSelectedPerson(person);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching form data', error);
      toast.error(error.message);
      setLoading(false);
    }
  };

  // Call search API when query changes
  useEffect(() => {
    const debouncedLoad = debounce(async (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) {
        setPeopleResults([]);
        return;
      }

      setLoadingPeople(true);
      try {
        const conversation_type = 'search-people';

        const data: any = {
          searchQuery: trimmed,
          typeCode: 1001,
          sql_data: {
            sql: 'EXEC [###schemaVariable].sp_SearchAllPeople ###whereClause',
            variables: [
              { data_column: '@LikeSearch', req_column: 'searchQuery' },
              { data_column: '@TypeCode', req_column: 'typeCode' },
            ],
            is_audit: true,
            audit_event_description: 'Viewed People -> Admin Conversations',
          },
        };

        const encryptedData = EncryptUtils.encryptedData(data);
        const response = await chatDataService('admin', 'people', conversation_type, {
          tk: encryptedData,
        });

        const rows = response?.data || [];

        const formatted = rows.map((row: any) => ({
          id: String(row.PersonId),
          typeCode: row.TypeCode,
          name:
            row.FullName ||
            [row.FirstName, row.MiddleName, row.LastName].filter(Boolean).join(' ') ||
            'NA',
          email: row.Email || 'NA',
          phoneNumber: row.PrimaryPhone || row.SecondaryPhone || 'NA',
          trackingNumber: row.TrackingNumber || 'NA',
        }));

        setPeopleResults(formatted);
      } catch (err: any) {
        console.error(err);
        toast.error(err.message || 'Failed to fetch people');
      } finally {
        setLoadingPeople(false);
      }
    }, 400);

    debouncedLoad(searchQuery);

    // Cleanup debounce on unmount
    return () => debouncedLoad.cancel();
  }, [searchQuery]);

  const buildHighlightChunks = (text: string) => {
    const matches = match(text, searchQuery);
    return parse(text, matches).map((part) => ({
      text: part.text,
      highlight: part.highlight,
    }));
  };

  const renderResults = () =>
    peopleResults?.map((person, index) => {
      const title = buildHighlightChunks(person.name || '');
      const path = buildHighlightChunks(
        `${person.email} • ${person.phoneNumber} • ${person.trackingNumber}`
      );

      return (
        <Box component="li" key={person.id || index} sx={{ display: 'flex' }}>
          <ResultItem
            title={title}
            path={path}
            groupLabel="Person"
            onClickItem={() => handleSelectPerson(person)}
          />
        </Box>
      );
    });

  const renderButton = (
    <Box
      display="flex"
      alignItems="center"
      onClick={search.onTrue}
      sx={{
        px: 2,
        py: 1,
        borderRadius: 1.5,
        bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.2),
        cursor: 'pointer',
        maxWidth: 280,
      }}
    >
      <SvgIcon sx={{ width: 20, height: 20, mr: 1 }}>
        <path
          fill="currentColor"
          d="m20.71 19.29l-3.4-3.39A7.92 7.92 0 0 0 19 11a8 8 0 1 0-8 8a7.92 7.92 0 0 0 4.9-1.69l3.39 3.4a1 1 0 0 0 1.42 0a1 1 0 0 0 0-1.42M5 11a6 6 0 1 1 6 6a6 6 0 0 1-6-6"
        />
      </SvgIcon>
      <Label
        sx={{
          fontSize: 12,
          color: 'grey.800',
          bgcolor: 'common.white',
          boxShadow: theme.customShadows.z1,
          display: { xs: 'none', sm: 'inline-flex' },
        }}
      >
        ⌘ People Search
      </Label>
    </Box>
  );

  return (
    <>
      {!onClose && renderButton}

      {/* People Search Dialog */}
      <Dialog
        fullWidth
        maxWidth="md"
        open={onClose ? true : search.value}
        onClose={onClose || handleClose}
        PaperProps={{ sx: { mb: 15, mr: 6 } }}
      >
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.vars.palette.divider}` }}>
          <InputBase
            fullWidth
            autoFocus
            placeholder="Search People..."
            value={searchQuery}
            onChange={handleSearch}
            startAdornment={
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" width={24} sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            endAdornment={
              loadingPeople ? (
                <CircularProgress size={20} />
              ) : (
                <IconButton onClick={onClose || handleClose}>
                  <Label sx={{ letterSpacing: 1, color: 'text.secondary' }}>Close</Label>
                </IconButton>
              )
            }
            inputProps={{ sx: { typography: 'h6' } }}
          />
        </Box>

        <Box sx={{ height: 600, overflow: 'hidden' }}>
          {searchQuery && !loadingPeople && peopleResults.length === 0 ? (
            <SearchNotFound query={searchQuery} sx={{ py: 10 }} />
          ) : (
            <Scrollbar
              sx={{
                px: 3,
                py: 2,
                height: '100%',
                overflowY: 'auto',
              }}
            >
              {renderResults()}
            </Scrollbar>
          )}
        </Box>
      </Dialog>

      {/* Person Details Dialog */}
      {(selectedPerson || loading) && (
        <Dialog
          open
          onClose={() => setSelectedPerson(null)}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { mr: 6 } }}
        >
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" width="100%">
              <Box>{adminFormData?.heading || 'Person Details'}</Box>{' '}
              <IconButton onClick={() => setSelectedPerson(null)}>
                <Iconify icon="mingcute:close-fill" />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            {loading ? (
              <Stack
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 5 }}
              >
                <PulseLoader size={12} />
              </Stack>
            ) : (
              adminFormData?.sub_sections?.map((section: any, idx: number) => {
                if (section.type === 'form-loader') {
                  return (
                    <Box sx={{ height: '100%' }} key={section.name}>
                      <FormLoader
                        key={section.name}
                        fetchDefaultValues
                        stageMessage={section.stageMessage}
                        formDataParsed={section}
                        module_name="person-details"
                        sub_module={section.name}
                        isAdmin
                        isFullApplicationEnabled={false}
                        person_id={selectedPerson?.id}
                      />
                    </Box>
                  );
                }
                return null;
              })
            )}
          </DialogContent>
          {/* <DialogActions>
            {adminFormData?.back_button && (
              <Button onClick={() => setSelectedPerson(null)} variant="outlined">
                Close
              </Button>
            )}
          </DialogActions> */}
        </Dialog>
      )}
    </>
  );
}
